import { useEffect } from 'react';

/**
 * Locks nested app-shell / settings scroll containers while a modal is open.
 * Chakra's body scroll lock alone does not stop overflowY:auto panes under the modal.
 */
export function useLockModalScroll(isOpen: boolean) {
    useEffect(() => {
        if (!isOpen) return;

        const locked: Array<{ el: HTMLElement; overflow: string; overflowY: string }> = [];
        const lockEl = (el: HTMLElement | null) => {
            if (!el || locked.some((entry) => entry.el === el)) return;
            locked.push({
                el,
                overflow: el.style.overflow,
                overflowY: el.style.overflowY,
            });
            el.style.overflow = 'hidden';
            el.style.overflowY = 'hidden';
        };

        lockEl(document.documentElement);
        lockEl(document.body);
        lockEl(document.querySelector('main'));

        let node: HTMLElement | null = document.querySelector('[data-settings-scroll]');
        while (node) {
            const style = window.getComputedStyle(node);
            if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) {
                lockEl(node);
            }
            node = node.parentElement;
        }

        const allowsNestedScroll = (target: EventTarget | null) => {
            if (!(target instanceof Element)) return false;
            if (
                target.closest(
                    '.chakra-popover__content, .chakra-menu__menu-list, [data-simplebar]'
                )
            ) {
                return true;
            }

            // Allow scrolling inside the modal's own overflow regions (e.g. bulk invite rows).
            const modalContent = target.closest('.chakra-modal__content');
            if (!modalContent) return false;

            let el: HTMLElement | null = target as HTMLElement;
            while (el && el !== modalContent) {
                const style = window.getComputedStyle(el);
                const overflowY = style.overflowY;
                if (
                    (overflowY === 'auto' || overflowY === 'scroll') &&
                    el.scrollHeight > el.clientHeight
                ) {
                    return true;
                }
                el = el.parentElement;
            }
            return false;
        };

        const preventBackgroundScroll = (event: WheelEvent | TouchEvent) => {
            if (allowsNestedScroll(event.target)) return;
            event.preventDefault();
        };

        document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
        document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

        return () => {
            document.removeEventListener('wheel', preventBackgroundScroll);
            document.removeEventListener('touchmove', preventBackgroundScroll);
            locked.forEach(({ el, overflow, overflowY }) => {
                el.style.overflow = overflow;
                el.style.overflowY = overflowY;
            });
        };
    }, [isOpen]);
}
